import React, { useState } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { registerExpertUser } from "./authSlice";
import * as Yup from "yup";
import Toast from "../../base/components/Toast";
import { useSelector } from "react-redux";
import { Input, Spin } from "antd";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";
import TermsConditionsModal from "../../components/TermsConditionsModal";
import SignupLayout, { ArrowIcon, SignupStepper } from "./components/SignupLayout";

export const EXPERT_STEP_KEYS = ["signup-step-account", "signup-step-profile", "signup-step-expertise"];

const initialValues = {
   email: "",
   password: "",
   confirmPassword: "",
   acceptTerms: false
};

function RegistrationOfExpert() {
   const { t } = useTranslation();
   const { executeRecaptcha } = useGoogleReCaptcha();
   const [modalVisible, setModalVisible] = useState(false);
   const loading = useSelector((state) => state.auth.isLoading);

   const dispatch = useDispatch();
   const navigate = useNavigate();

   const validationSchema = Yup.object().shape({
      email: Yup.string()
         .email(t("invalid-email-address-validation"))
         .required(t("email-required-validation")),
      password: Yup.string()
         .min(8, t("password-min-length-validation"))
         .matches(
            /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*\d).*$/,
            t("password-complexity-validation")
         )
         .required(t("password-required-validation")),
      confirmPassword: Yup.string()
         .oneOf([Yup.ref("password"), null], t("passwords-must-match-validation"))
         .required(t("confirm-password-required-validation")),
      acceptTerms: Yup.boolean().oneOf([true], t("accept-terms-required-validation"))
   });

   const onSubmit = async (values) => {
      if (!executeRecaptcha) {
         return;
      }

      const recaptchaToken = await executeRecaptcha("login");
      try {
         dispatch(
            registerExpertUser(
               values,
               () => {
                  navigate(`/auth/confirm-code?email=${encodeURIComponent(values.email)}`);
               },
               recaptchaToken
            )
         );
      } catch (error) {
         if (error.response || error.response.data || error.response.data.errors) {
            const errorMessage = error.response.data.errors.message;
            Toast.error(errorMessage);
         }
      }
   };

   const openTermsModal = () => {
      setModalVisible(!modalVisible);
   };

   const fieldError = (errors, touched, name) =>
      errors[name] && touched[name] && <div className="error-message">{errors[name]}</div>;

   return (
      <SignupLayout showcase="expert" backTo="/auth/registration">
         <p className="kap-org-signup__eyebrow">{t("signup-create-account-as-an")}</p>
         <h1 className="kap-org-signup__title">{t("profile-expert-label")}</h1>

         <SignupStepper steps={EXPERT_STEP_KEYS.map((key) => t(key))} current={0} />

         <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
         >
            {({ values, handleChange, handleBlur, errors, touched }) => (
               <Form className="kap-login__form" style={{ maxWidth: "none" }}>
                  <p className="kap-org-signup__section">{t("login-details-title")}</p>
                  <div className="kap-login__fields">
                     <div className="form-group">
                        <label className="kap-login__label kap-login__label--required">{t("email")}</label>
                        <Field
                           placeholder={t("email-placeholder")}
                           className="form-control"
                           type="email"
                           name="email"
                           autoComplete="off"
                        />
                        {fieldError(errors, touched, "email")}
                     </div>
                     <div className="form-group">
                        <label className="kap-login__label kap-login__label--required">{t("password-label")}</label>
                        <Input.Password
                           placeholder={t("password-label")}
                           name="password"
                           value={values.password}
                           onChange={handleChange}
                           onBlur={handleBlur}
                           maxLength={16}
                        />
                        {fieldError(errors, touched, "password")}
                     </div>
                     <div className="form-group">
                        <label className="kap-login__label kap-login__label--required">{t("confirm-password-label")}</label>
                        <Input.Password
                           placeholder={t("password-label")}
                           name="confirmPassword"
                           value={values.confirmPassword}
                           onChange={handleChange}
                           onBlur={handleBlur}
                           maxLength={16}
                        />
                        {fieldError(errors, touched, "confirmPassword")}
                     </div>
                  </div>
                  <div className="kap-org-signup__terms">
                     <label className="d-flex align-items-start m-0">
                        <Field type="checkbox" name="acceptTerms" />
                        <span>
                           {t("terms-agreement-prefix-text")}{" "}
                           <Link onClick={openTermsModal} to="#">
                              {t("terms-of-service-privacy-policy-link")}
                           </Link>{" "}
                           {t("and-conjunction-text")}{" "}
                           <Link onClick={openTermsModal} to="#">
                              {t("cookie-policy-link")}
                           </Link>
                        </span>
                     </label>
                     {fieldError(errors, touched, "acceptTerms")}
                  </div>

                  <button
                     type="submit"
                     className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                     disabled={loading}
                  >
                     {loading ? (
                        <Spin />
                     ) : (
                        <>
                           {t("continue-button")}
                           <ArrowIcon />
                        </>
                     )}
                  </button>
               </Form>
            )}
         </Formik>
         <TermsConditionsModal visible={modalVisible} onCancel={openTermsModal} />
      </SignupLayout>
   );
}
export default RegistrationOfExpert;
